import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {string | null} - The option value currently chosen in the sticky dropdown */
  #currentStickyOptionValue = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // Custom sticky variant selector (dropdown) setup
    this.#setupStickySelector();
    this.#scheduleStickySwatchSync();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar.
   *
   * This store renders variants via the Kaching Bundles app, which ignores the
   * hidden id field and drives the real add-to-cart button from its own internal
   * state. Puppet-clicking the real button therefore always adds Kaching's
   * selected variant, not the one chosen in the sticky dropdown. To reliably add
   * the chosen variant we bypass the button entirely and POST straight to
   * /cart/add.js with the sticky variant id, then refresh and open the cart
   * drawer from the returned section HTML.
   */
  handleAddToCartClick = async () => {
    const variantId = this.dataset.currentVariantId;
    if (!variantId) return;

    const button = this.refs.addToCartButton;
    if (button.disabled) return;
    button.disabled = true;

    // Section ids the cart drawer needs so it can re-render after the add.
    const sectionIds = this.#getCartSectionIds();

    try {
      const body = {
        items: [{ id: Number(variantId), quantity: this.#currentQuantity || 1 }],
      };
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`cart/add failed: ${response.status}`);

      const data = await response.json();

      // Update any cart drawer sections that came back in the response.
      this.#renderCartSections(data.sections);

      // Notify the theme that the cart changed so counts/drawers stay in sync.
      this.#dispatchCartUpdate(data);

      // Play the added-state + fly-to-cart animation.
      await this.#playAddedAnimation();

      // Open the cart drawer for the shopper.
      this.#openCartDrawer();
    } catch (error) {
      console.error('[sticky-add-to-cart] add to cart failed:', error);
    } finally {
      button.disabled = false;
    }
  };

  /**
   * Collects the section ids that render cart UI (drawer + header cart icon),
   * so /cart/add.js can return fresh HTML for them in one round-trip.
   * @returns {string[]}
   */
  #getCartSectionIds() {
    const ids = new Set();

    // Elements whose enclosing section must be re-rendered after an add:
    // the cart drawer, the cart items, and the header cart icon/bubble.
    const selectors = [
      'cart-drawer-component',
      'cart-items-component',
      'cart-icon',
      '.cart-bubble',
      '.header-actions__cart-icon',
      '[id^="cart-drawer"]',
    ];

    document.querySelectorAll(selectors.join(', ')).forEach((el) => {
      const sectionEl = el.closest('.shopify-section') || el;
      const id = sectionEl.id?.replace('shopify-section-', '');
      if (id) ids.add(id);

      // Some drawers expose the section id directly on a data attribute.
      const dataId = el.getAttribute('data-section-id');
      if (dataId) ids.add(dataId);
    });

    return [...ids];
  }

  /**
   * Replaces the HTML of cart sections returned by /cart/add.js.
   * @param {Record<string, string> | undefined} sections
   */
  #renderCartSections(sections) {
    if (!sections) return;

    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const target =
        document.getElementById(`shopify-section-${id}`) ||
        document.querySelector(`[data-section-id="${id}"]`);
      if (!target) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const newInner = fresh.querySelector(`#shopify-section-${id}`) || fresh.body.firstElementChild;
      if (newInner) {
        try {
          morph(target, newInner);
        } catch (e) {
          target.innerHTML = newInner.innerHTML;
        }
      }
    });
  }

  /**
   * Dispatches the theme's cart-update event(s) so cart bubbles and other
   * listeners refresh. Falls back gracefully if the event isn't available.
   * @param {any} data - The cart/add.js response payload
   */
  #dispatchCartUpdate(data) {
    try {
      if (StandardEvents?.cartLinesUpdate) {
        document.dispatchEvent(
          new CustomEvent(StandardEvents.cartLinesUpdate, {
            bubbles: true,
            detail: { data, source: 'sticky-add-to-cart' },
          })
        );
      }
    } catch (e) {
      /* no-op */
    }

    // Legacy fallback used by many themes/apps.
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {
      /* no-op */
    }

    // Directly refresh the header cart count bubble, since <cart-icon> doesn't
    // always pick up the count from our dispatched event.
    this.#updateCartIcon();
  }

  /**
   * Updates the header cart-icon count bubble from the authoritative cart state.
   * Fetches /cart.js for the true item_count so the number always matches the
   * drawer. Applies the count immediately and again on the next frames, so a
   * cart section morph can't leave the bubble blank.
   * @returns {Promise<void>}
   */
  async #updateCartIcon() {
    let itemCount = null;

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const cart = await res.json();
        itemCount = typeof cart.item_count === 'number' ? cart.item_count : null;
      }
    } catch (e) {
      /* no reliable count available */
    }

    if (itemCount == null) return;

    // Apply now, then re-apply shortly after in case a section morph overwrote it.
    this.#applyCartCount(itemCount);
    requestAnimationFrame(() => this.#applyCartCount(itemCount));
    setTimeout(() => this.#applyCartCount(itemCount), 250);
  }

  /**
   * Writes the given count into all cart bubbles and toggles icon visibility.
   * @param {number} itemCount
   */
  #applyCartCount(itemCount) {
    const countText = String(itemCount);

    // Set the number on every count element the theme uses.
    document
      .querySelectorAll('.cart-bubble__text-count, [data-cart-count]')
      .forEach((el) => {
        el.textContent = countText;
      });

    // cart-bubble__text may contain the count plus a visually-hidden label;
    // only replace it if it has no child elements (a plain text node).
    document.querySelectorAll('.cart-bubble__text').forEach((el) => {
      if (el.children.length === 0) el.textContent = countText;
    });

    // Toggle has-cart state so the bubble is shown/hidden correctly.
    document.querySelectorAll('cart-icon, .header-actions__cart-icon').forEach((icon) => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });

    // Make sure the bubble itself isn't left hidden by a stale render.
    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      if (!(bubble instanceof HTMLElement)) return;
      if (itemCount > 0) {
        bubble.hidden = false;
        // Clear any inline display:none we (or a prior render) may have set.
        if (bubble.style.display === 'none') bubble.style.display = '';
      }
    });
  }

  /**
   * Opens the cart drawer using whichever mechanism the theme exposes.
   */
  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component, cart-drawer, [class*="cart-drawer"]');

    // Preferred: a component API if present.
    if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function') {
      /** @type {any} */ (drawer).open();
      return;
    }

    // Common pattern: a dialog inside the drawer.
    const dialog = drawer?.querySelector('dialog');
    if (dialog && typeof (/** @type {any} */ (dialog).showModal) === 'function') {
      try {
        /** @type {any} */ (dialog).showModal();
        return;
      } catch (e) {
        /* fall through */
      }
    }

    // Fallback: click the cart icon to trigger the theme's own open handler.
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (cartIcon instanceof HTMLElement) cartIcon.click();
  }

  /**
   * Plays the sticky button's added state and the fly-to-cart animation.
   * @returns {Promise<void>}
   */
  async #playAddedAnimation() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        // Re-bind the custom dropdown after the sticky bar content is morphed
        this.#setupStickySelector();
        this.#scheduleStickySwatchSync();

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  // ---------------------------------------------------------------------------
  // Custom sticky variant selector (dropdown) — restored after theme update
  // ---------------------------------------------------------------------------

  /**
   * Wires up the custom sticky variant dropdown: opening/closing the menu and
   * handling option selection.
   */
  #setupStickySelector() {
    const trigger = this.querySelector('[data-sticky-selector-trigger]');
    const menu = this.querySelector('[data-sticky-selector-menu]');
    const label = this.querySelector('[data-sticky-selector-label]');
    const triggerSwatch = this.querySelector('[data-sticky-selector-swatch]');
    const options = this.querySelectorAll('[data-sticky-option-value]');

    if (!trigger || !menu || !label || !triggerSwatch || options.length === 0) return;

    // Seed the currently chosen option value from the pre-selected option.
    const preselected = this.querySelector('[data-sticky-option-value].is-selected');
    if (preselected) {
      this.#currentStickyOptionValue = preselected.getAttribute('data-sticky-option-value');
    }

    trigger.onclick = (event) => {
      event.stopPropagation();

      const isOpen = !menu.hasAttribute('hidden');

      if (isOpen) {
        menu.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        menu.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
      }
    };

    options.forEach((option) => {
      option.onclick = () => {
        const value = option.getAttribute('data-sticky-option-value');
        const variantId = option.getAttribute('data-sticky-variant-id');
        const variantAvailable = option.getAttribute('data-sticky-variant-available') === 'true';
        const variantTitle = option.getAttribute('data-sticky-variant-title');
        const variantPrice = option.getAttribute('data-sticky-variant-price');
        const variantComparePrice = option.getAttribute('data-sticky-variant-compare-price');
        const variantImage = option.getAttribute('data-sticky-variant-image');
        const variantImageAlt = option.getAttribute('data-sticky-variant-image-alt');
        const optionSwatch = option.querySelector('[data-sticky-option-swatch]');

        if (!value || !variantId) return;

        this.dataset.currentVariantId = variantId;
        this.dataset.variantAvailable = variantAvailable ? 'true' : 'false';
        this.#currentStickyOptionValue = value;
        label.textContent = value;

        if (triggerSwatch instanceof HTMLElement && optionSwatch instanceof HTMLElement) {
          triggerSwatch.innerHTML = optionSwatch.innerHTML;
        }

        this.#applyStickyVariantData({
          value,
          variantTitle,
          variantPrice,
          variantComparePrice,
          variantImage,
          variantImageAlt,
          variantAvailable,
        });

        options.forEach((item) => {
          item.classList.remove('is-selected');
          item.setAttribute('aria-selected', 'false');
        });

        option.classList.add('is-selected');
        option.setAttribute('aria-selected', 'true');

        this.refs.addToCartButton.disabled = !variantAvailable;

        menu.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', 'false');
      };
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Node)) return;

        if (!this.contains(event.target)) {
          menu.setAttribute('hidden', '');
          trigger.setAttribute('aria-expanded', 'false');
        }
      },
      { signal: this.#abortController.signal }
    );
  }

  /**
   * Applies the selected variant's data (title, price, image) to the sticky bar UI.
   */
  #applyStickyVariantData({
    value,
    variantTitle,
    variantPrice,
    variantComparePrice,
    variantImage,
    variantImageAlt,
    variantAvailable,
  }) {
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const priceElement = this.querySelector('[data-testid="sticky-price-display"]');
    const productImage = this.refs.productImage;

    if (variantTitleElement) {
      variantTitleElement.textContent = variantTitle || value;
    }

    if (priceElement) {
      if (variantComparePrice) {
        priceElement.innerHTML = `
          <span class="price-item price-item--sale">${variantPrice}</span>
          <s class="compare-at-price">${variantComparePrice}</s>
        `;
      } else {
        priceElement.innerHTML = `
          <span class="price-item price-item--regular">${variantPrice}</span>
        `;
      }
    }

    if (productImage instanceof HTMLImageElement && variantImage) {
      productImage.src = variantImage;
      productImage.alt = variantImageAlt || '';
    }

    this.refs.addToCartButton.disabled = !variantAvailable;
  }

  /**
   * Finds the source swatch elements from third-party variant apps (AVP / Kaching).
   * @returns {HTMLElement[]}
   */
  #getSourceSwatchElements() {
    const avp = Array.from(
      document.querySelectorAll('label.avp-productoptionswatchwrapper.apo-swatch-wrapper')
    );

    if (avp.length) {
      return avp
        .map((option) => option.querySelector('.ap-options__swatch, .avp-productoptionswatch'))
        .filter((el) => el instanceof HTMLElement);
    }

    const kaching = Array.from(
      document.querySelectorAll('.kaching-bundles__swatch-dropdown.kaching-bundles-option')
    );

    if (kaching.length) {
      return kaching
        .map((option) => option.querySelector('.kaching-bundles__swatch'))
        .filter((el) => el instanceof HTMLElement);
    }

    return [];
  }

  /**
   * Copies swatch visuals from the source app swatches into each sticky option.
   */
  #syncAllStickyOptionSwatches() {
    const stickyOptions = Array.from(this.querySelectorAll('[data-sticky-option-value]'));
    const sourceSwatches = this.#getSourceSwatchElements();

    stickyOptions.forEach((option, index) => {
      const swatchHolder = option.querySelector('[data-sticky-option-swatch]');
      const sourceSwatch = sourceSwatches[index];

      if (!(swatchHolder instanceof HTMLElement) || !(sourceSwatch instanceof HTMLElement)) return;

      swatchHolder.innerHTML = sourceSwatch.outerHTML;
    });
  }

  /**
   * Syncs the trigger swatch to match the currently selected option's swatch.
   */
  #syncInitialStickyTriggerSwatch() {
    const triggerSwatch = this.querySelector('[data-sticky-selector-swatch]');
    const selectedOption = this.querySelector('[data-sticky-option-value].is-selected');
    const selectedOptionSwatch = selectedOption?.querySelector('[data-sticky-option-swatch]');

    if (!(triggerSwatch instanceof HTMLElement) || !(selectedOptionSwatch instanceof HTMLElement)) return;

    triggerSwatch.innerHTML = selectedOptionSwatch.innerHTML;
  }

  /**
   * Retries swatch syncing a few times, since third-party swatch apps may
   * render after this component initializes.
   */
  #scheduleStickySwatchSync() {
    let attempts = 0;

    const run = () => {
      this.#syncAllStickyOptionSwatches();
      this.#syncInitialStickyTriggerSwatch();

      const triggerSwatch = this.querySelector('[data-sticky-selector-swatch]');
      const hasVisualSwatch =
        triggerSwatch instanceof HTMLElement &&
        !!triggerSwatch.querySelector(
          '.ap-options__swatch, .avp-productoptionswatch, .kaching-bundles__swatch, img'
        );

      if (hasVisualSwatch || attempts >= 10) return;

      attempts += 1;
      setTimeout(run, 150);
    };

    run();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}